import { gql, useSubscription } from "@apollo/client";
import { Box, Grid, Pagination, useTheme } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid";
import { CloudTextField } from "components/atoms/CloudTextField";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { RobotListCard } from "components/modules/RobotListCard";
import { BatteryCell } from "components/modules/tableCells/BatteryCell";
import { OrganizationCell } from "components/modules/tableCells/OrganizationCell";
import { RobotCell } from "components/modules/tableCells/RobotCell";
import { TechnicalServiceNeededCell } from "components/modules/tableCells/TechnicalServiceNeededCell";
import { UVDRobotStatusCell } from "components/modules/tableCells/UVDRobotStatusCell";
import { ForbiddenPage } from "components/pages/ForbiddenPage";
import { IRobot, UvdRobot } from "gql/graphql";
import _ from "lodash";
import { ReactElement, useEffect, useState } from "react";
import { IoSearch } from "react-icons/io5";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { getIsForbidden } from "utils/privileges/privilegeUtils";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { ON_ROBOT_CHANGED } from "./UnassignedRobots";
import { getShowTechnicalServiceNeeded } from "utils/UVD/UVDUtils";

export const ASSIGNED_ROBOTS_QUERY = gql(`
  query AssignedRobots(
    $organizationIds: [String!]
    $search: String
  ) {
    robots(
      isActive: true
      organizationIds: $organizationIds
      search: $search
    ) {
      robots {
        id
        serialNumber
        robotType
        active {
          organization {
            id
            name
            country
          }
          nickname
        }
        ... on UvdRobot {
          status {
            batteryMergerPercentage
            lastTimeActive
            job {
              state
              type
            }
          }
          version {
            seto
          }
        }
      }
    }
  }
`);

export const ASSIGNED_ROBOTS_COUNT_QUERY = gql(`
  query AssignedRobotsCount($organizationId: String) {
    robotCount(isActive: true, organizationId: $organizationId)
  }
`);

const columns: GridColDef[] = [
  {
    field: "robot",
    maxWidth: 250,
    minWidth: 150,
    flex: 1,
    headerName: "Robot",
    valueGetter: (params) => params.row?.serialNumber,
    renderCell: (params: GridRenderCellParams) => (
      <RobotCell robot={params.row} showStatusDot disableHoverOnRobotName />
    ),
  },
  {
    field: "organization",
    maxWidth: 250,
    minWidth: 150,
    flex: 1,
    headerName: "Organization",
    valueGetter: (params) => params.row.active?.organization?.name,
    renderCell: (params: GridRenderCellParams) => (
      <OrganizationCell
        organization={params.row.active?.organization}
        hideAvatar
      />
    ),
  },
  {
    field: "battery",
    flex: 1,
    maxWidth: 200,
    headerName: "Battery",
    valueGetter: (params) => params.row?.status?.batteryMergerPercentage,
    renderCell: (params) => (
      <BatteryCell
        robot={params.row}
        hideBattery={false}
        batteryMergerPercentage={params.row?.status?.batteryMergerPercentage}
      />
    ),
  },
  {
    field: "status",
    flex: 1,
    maxWidth: 250,
    headerName: "Status",
    renderCell: (params) => <UVDRobotStatusCell robot={params.row} />,
    valueGetter(params) {
      return params?.row?.status?.lastTimeActive
        ? new Date(params.row.status.lastTimeActive).getTime()
        : null;
    },
  },
  {
    field: "technical-service",
    headerName: "Technical Service",
    renderCell: TechnicalServiceNeededCell,
    maxWidth: 150,
    flex: 1,
    valueGetter(params) {
      const { showWarning } = getShowTechnicalServiceNeeded(params.row);
      return showWarning || !params.row.status;
    },
  },
  {
    field: "sw-version",
    maxWidth: 100,
    flex: 1,
    headerName: "SW Version",
    align: "right",
    headerAlign: "right",
    valueGetter(params) {
      return params.row.version?.seto || "";
    },
  },
];

export const AssignedRobots = ({
  columns: columnsProps,
  label,
  showGrid = false,
}: {
  columns?: GridColDef[];
  label?: string | ReactElement;
  showGrid?: boolean;
}) => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const theme = useTheme();

  const pageSize = 25;

  const { data, loading, error } = useQueryWithSnack(ASSIGNED_ROBOTS_QUERY, {
    variables: {
      ...(orgId ? { organizationIds: [orgId] } : {}),
      search,
    },
    pollInterval: 10000,
  });

  const robots = data?.robots.robots as IRobot[] | undefined;

  const sub = useSubscription(ON_ROBOT_CHANGED);
  useEffect(() => {
    if (robots && sub.data && sub.data.robotChanged) {
      const subRobot = sub.data.robotChanged as UvdRobot;
      const foundRobot = robots.find((o) => o.id == subRobot.id);
      if (foundRobot) {
        _.merge(foundRobot, subRobot);
      }
    }
  }, [robots, sub.data]);

  if (getIsForbidden(error)) return <ForbiddenPage />;

  return (
    <WidgetPaper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <WidgetHeader
          label={
            label ?? <FormattedMessage id="robots" defaultMessage="Robots" />
          }
        />
        <CloudTextField
          sx={{ mt: 2, width: "50%" }}
          placeholder="Type serial number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <>
                <IoSearch size={20} color={theme.palette.primary.main} />
              </>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          Loading...
        </Box>
      ) : showGrid ? (
        <>
          <Grid container spacing={6} sx={{ mt: 2, mb: 2 }}>
            {(robots ?? []).map((robot) => (
              <Grid item key={robot.id}>
                <RobotListCard
                  robot={robot}
                  onClick={() => navigate(robot.id)}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={Math.ceil((robots?.length ?? 0) / pageSize)}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              color="primary"
            />
          </Box>
        </>
      ) : (
        <CloudDataGrid
          onRowClick={(params: GridRowParams) => navigate(params.row.id)}
          rows={robots ?? []}
          columns={columnsProps ?? columns}
          loading={loading}
          pageSize={pageSize}
          page={page}
          onPageChanged={(pageNumber) => {
            setPage(pageNumber);
          }}
          rowCount={robots?.length ?? 0}
          initialState={{
            sorting: {
              sortModel: [{ field: "robot", sort: "asc" }],
            },
          }}
        />
      )}
    </WidgetPaper>
  );
};
